/**
 * DetailPageLayout.tsx
 *
 * The patient-chart shell: sub-navigation, content, record detail. Below `lg` the
 * sub-nav becomes a horizontal strip above the content and the detail panel moves
 * into a sheet, leaving the content column the full viewport.
 *
 * ── Why `lg` and not `md` ───────────────────────────────────────────────────────
 * At 768px the nav rail, a 256px sub-nav and a 400px detail panel leave roughly 56px
 * of content. The sub-nav reflows in place rather than going off-canvas because it is
 * the chart's primary navigation — putting it behind a tap would hide the only way to
 * move around the record.
 *
 * ── Why these are JS branches and not `max-lg:` classes ─────────────────────────
 * A printed page lays out near 816px, so a `max-lg:` escape fires while printing.
 * This surface owns three of the four places the app prints a live document, so a
 * class escape would silently drop the detail panel and the desktop sub-nav from
 * every printed chart. The breakpoint hook reads the *screen*, so print keeps the
 * desktop form.
 *
 * ── The sub-nav's collapse state lives here now ─────────────────────────────────
 * It used to come from this layout's nested sidebar provider, and all three providers
 * in the tree read and write one persisted key — so collapsing the sub-nav persisted
 * "global nav collapsed" for the next page load. The layout now owns that state under
 * its own key and passes it down, which keeps the two independent.
 *
 * The nested provider itself has to stay: the sub-nav's desktop branch is built from
 * the shadcn sidebar primitives, and their `useSidebar` throws outside a provider.
 * One consequence is not fixed by this change — the provider registers its own
 * Ctrl/Cmd-B handler, and that path still writes the shared key. Removing it means
 * replacing the menu-button primitive, which would change the desktop DOM.
 */
import { SidebarProvider } from "@/components/ui/sidebar";
import { Loader2, PanelRightOpen } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { sidebarPrefs } from "@/lib/sidebarPrefs";
import { DetailSubNavCollapseProvider } from "./DetailSubNav";
import { OffCanvasPanel, OffCanvasRegion } from "./OffCanvasPanel";

/** Distinct from the global navigation's key, which is the point of having it. */
const SUBNAV_STORAGE_KEY = "nh:sidebar:detail-subnav-open";

interface DetailPageLayoutProps {
  subNav: React.ReactNode;
  children: React.ReactNode;
  rightPanel: React.ReactNode;
  loading?: boolean;
  /**
   * Change this when the user asks to see the panel — picking a lab result, say.
   * Omit it and the panel never auto-opens; the Details control is then the only
   * way in, which is the safe default for a page whose panel comes and goes for
   * reasons the user did not ask for. See the note on the effect below.
   */
  panelOpenSignal?: number;
}

export function DetailPageLayout({
  subNav,
  children,
  rightPanel,
  loading = false,
  panelOpenSignal,
}: DetailPageLayoutProps) {
  const stacked = useIsBelow("lg");
  const hasPanel = rightPanel != null;

  // Seeded from the old shared key so the first load after this ships looks exactly
  // like the last one. After that the two keys go their own ways.
  const seed = useMemo(() => sidebarPrefs.readNav(true), []);
  const [subNavOpen, setSubNavOpen] = useLocalStorage(SUBNAV_STORAGE_KEY, seed);

  const [panelOpen, setPanelOpen] = useState(false);

  const lastSignal = useRef(panelOpenSignal);
  useEffect(() => {
    // Opening is driven by `panelOpenSignal`, not by the panel node appearing.
    //
    // Reading "a panel exists where none did" as "show me this" is what SCRUM-1384
    // was. On the patient chart exactly one section — demographics, the default tab,
    // labelled "Details" — renders a null panel, so every move off that tab flipped
    // this false→true and threw the sheet over the tab the user had just chosen.
    // Returning to Details re-armed it, so it fired again on the next tab. The node's
    // presence tracks which section is open; it was never evidence of intent.
    //
    // The ref stays in sync even while inline, so resizing across the boundary with a
    // panel already present does not spring the sheet open.
    if (stacked) {
      const requested =
        panelOpenSignal !== undefined && panelOpenSignal !== lastSignal.current;

      if (hasPanel && requested) setPanelOpen(true);
      else if (!hasPanel) setPanelOpen(false);
    }
    lastSignal.current = panelOpenSignal;
  }, [hasPanel, stacked, panelOpenSignal]);

  useEffect(() => {
    if (!stacked) setPanelOpen(false);
  }, [stacked]);

  // The rail reads this instead of the shared sidebar context, so collapsing it no
  // longer writes the global navigation's key.
  const subNavCollapse = useMemo(
    () => ({ collapsed: !subNavOpen, toggle: () => setSubNavOpen((open) => !open) }),
    [subNavOpen, setSubNavOpen],
  );

  const content = loading ? (
    <div className="flex h-full items-center justify-center bg-white">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ) : (
    children
  );

  return (
    <OffCanvasRegion>
      <SidebarProvider
        open={subNavOpen}
        onOpenChange={setSubNavOpen}
        className={cn("h-full min-h-0 overflow-hidden", stacked && "flex-col")}
        style={
          {
            "--sidebar-width": "16rem",
            "--sidebar-width-icon": "3.5rem",
          } as React.CSSProperties
        }
      >
        <DetailSubNavCollapseProvider value={subNavCollapse}>
          {stacked ? (
            <div className="flex w-full min-w-0 shrink-0 items-start border-b border-slate-200 bg-white">
              <div className="min-w-0 flex-1">{subNav}</div>

              {hasPanel && (
                <div className="flex shrink-0 items-center self-stretch border-l border-slate-200 px-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<PanelRightOpen className="h-4 w-4" />}
                    onClick={() => setPanelOpen(true)}
                  >
                    Details
                  </Button>
                </div>
              )}
            </div>
          ) : (
            subNav
          )}
        </DetailSubNavCollapseProvider>

        <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden bg-white">
          <section className="min-w-0 flex-1 min-h-0 overflow-hidden bg-white">
            {content}
          </section>
          {!stacked && rightPanel}
        </div>

        {stacked && hasPanel && (
          <OffCanvasPanel
            id="detail-right-panel"
            isOpen={panelOpen}
            onClose={() => setPanelOpen(false)}
            side="right"
            title="Details"
            size="wide"
          >
            {rightPanel}
          </OffCanvasPanel>
        )}
      </SidebarProvider>
    </OffCanvasRegion>
  );
}
